'use client';

import { useState, useEffect } from 'react';
import { Box, Grid, TextField, CircularProgress, Button } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormDialog from '../../../../components/FormDialog';
import PickerField from '../../../../components/PickerField';
import { useNotifier } from '../../../../components/Notifier';
import { useLanguage } from '../../../../utils/i18n';
import { createConstructionType, updateConstructionType, getConstructionTypeById } from '../../../../stores/constructionTypeStore';
import { useCompanyDataList } from '../../../../stores/companyStore';
import usePickerPagination from '../../../../hooks/usePickerPagination';
import MasterCompanyAddEdit from '../../../master-data/company/MasterCompanyAddEdit';

const ConstructionTypeAddEdit = ({ open, id = null, onClose, onSuccess }) => {
  const notify = useNotifier();
  const { t } = useLanguage();

  const [initialValues, setInitialValues] = useState({
    company: typeof window !== 'undefined' ? localStorage.getItem('companyId') || '' : '',
    name: '',
    description: ''
  });

  const [loading, setLoading] = useState(false);

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  useEffect(() => {
    if (!open) return;

    let mounted = true;
    const fetch = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await getConstructionTypeById(id);
        if (!mounted) return;
        if (data) {
          setInitialValues((prev) => ({
            ...prev,
            company: data.company_id ?? data.company ?? prev.company,
            name: data.name ?? prev.name,
            description: data.description ?? prev.description
          }));
        }
      } catch (err) {
        console.error('Failed to load construction type:', err);
        notify({ message: t('constructionType.failedLoadData', 'Failed to load data'), severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => {
      mounted = false;
    };
  }, [id, open, notify, t]);

  const validationSchema = Yup.object().shape({
    company: Yup.string().required(t('general.validationCompanyRequired', 'Company is required')),
    name: Yup.string().required(t('general.validationNameRequired', 'Name is required'))
  });

  const handleClose = () => {
    formik.resetForm();
    if (onClose) onClose();
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          company_id: Number(values.company),
          name: values.name ?? '',
          description: values.description ?? ''
        };

        if (id) {
          await updateConstructionType(id, payload);
          notify({ message: t('general.updateSuccess', 'Data updated successfully'), severity: 'success' });
        } else {
          await createConstructionType(payload);
          notify({ message: t('general.createSuccess', 'Data added successfully'), severity: 'success' });
        }

        formik.resetForm();
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.message || 'Failed to save data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const dialogActions = (
    <>
      <Button onClick={handleClose} disabled={formik.isSubmitting} sx={{ color: 'text.secondary', textTransform: 'none' }}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={formik.handleSubmit}
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{ textTransform: 'none', borderRadius: 1, px: 3, fontWeight: 700 }}
      >
        {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
      </Button>
    </>
  );

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={
        id
          ? t('general.edit', 'Edit') + ' ' + t('constructionType.title', 'Construction Type')
          : t('general.add', 'Add') + ' ' + t('constructionType.title', 'Construction Type')
      }
      maxWidth="sm"
      fullScreenMode={false}
      actions={dialogActions}
    >
      <Box sx={{ width: '100%', pt: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2.5}>
              <Grid item size={{ xs: 12 }}>
                <PickerField
                  label={t('general.company', 'Company') + ' *'}
                  name="company"
                  allowAdd
                  AddComponent={MasterCompanyAddEdit}
                  value={formik.values.company}
                  onChange={(val) => formik.setFieldValue('company', val)}
                  onBlur={() => formik.setFieldTouched('company', true)}
                  error={formik.touched.company && Boolean(formik.errors.company)}
                  helperText={formik.touched.company && formik.errors.company}
                  {...companyPickerParams}
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <TextField
                  label={t('general.name', 'Name') + ' *'}
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  size="medium"
                  fullWidth
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <TextField
                  label={t('general.description', 'Description')}
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  size="medium"
                  fullWidth
                  multiline
                  minRows={3}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                />
              </Grid>
            </Grid>
          </form>
        )}
      </Box>
    </FormDialog>
  );
};

export default ConstructionTypeAddEdit;
